import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Avatar,
  Divider,
  Stack,
  IconButton,
  alpha,
  useTheme,
} from "@mui/material";
import { Add, ChatBubbleOutline, Send } from "@mui/icons-material";
import { useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import CustomInput from "../../FormFields/CustomInput/CustomInput";
import SearchableSelect from "../../FormFields/CustomSelect/SearchableSelect";
import { commentTypeOptions } from "../incident.config";
import { createIncidentCommentary } from "../../../api/services/notification";
import { QK } from "../../../base/qk";
import { formatDateTime } from "../../../base/FormatTime";

const Commentary = ({ formData, handleFormChange }) => {
  const theme = useTheme();
  const [commentText, setCommentText] = useState("");
  const [commentType, setCommentType] = useState("");
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const comments = formData?.commentaries || [];

  const handleAddComment = async () => {
    if (!commentText.trim() || !commentType) return;

    try {
      const newComment = {
        comment: commentText,
        commentary_type: commentType,
      };
      const response = await createIncidentCommentary(formData?.id, newComment);

      if (response.status === 201) {
        enqueueSnackbar("Comment added successfully", {
          variant: "success",
        });
        queryClient.invalidateQueries([QK.incidentDetails, formData?.id]);
        setCommentText("");
        setCommentType("");
      } else {
        enqueueSnackbar("Failed to add comment", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Failed to add comment", { variant: "error" });
    }
  };

  const getTypeLabel = (value) =>
    commentTypeOptions.find((opt) => opt.value === value)?.label || "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ChatBubbleOutline
            sx={{ fontSize: 20, color: theme.palette.primary.main }}
          />
        </Box>
        <Box>
          <Typography
            variant="h6"
            fontWeight={700}
            color="#1A1A2E"
            fontSize="0.95rem"
          >
            Commentary
          </Typography>
          <Typography variant="caption" color="#6B7280" fontSize="0.7rem">
            Add notes and updates about this incident
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          border: "1px solid #EAECF0",
          borderRadius: "10px",
          p: 2,
          bgcolor: "#FAFBFC",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CustomInput
          name="commentary"
          label="Add commentary"
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          multiline
          rows={2}
          placeholder="Type your comment..."
        />

        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: "1 1 240px", minWidth: 200 }}>
            <SearchableSelect
              name="commentType"
              label="Type of commentary"
              value={
                commentTypeOptions.find((o) => o.value === commentType) || null
              }
              onChange={(opt) => setCommentType(opt?.value ?? "")}
              options={commentTypeOptions}
              placeholder="Select type..."
              getOptionLabel={(o) => o.label}
              getOptionValue={(o) => o.value}
            />
          </Box>

          <Button
            variant="contained"
            size="small"
            startIcon={<Send sx={{ fontSize: 16 }} />}
            onClick={handleAddComment}
            disabled={!commentText.trim() || !commentType}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              boxShadow: "none",
              px: 2.5,
              height: 40,
              mt: 0.5,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Add Comment
          </Button>
        </Box>
      </Box>

      <Divider />

      {comments.length > 0 ? (
        <Stack spacing={1.5}>
          {comments.map((comment, index) => (
            <Box
              key={index}
              sx={{
                border: "1px solid #EAECF0",
                borderRadius: "10px",
                p: 2,
                bgcolor: "#FFFFFF",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      fontSize: "0.8rem",
                      fontWeight: 700,
                    }}
                  >
                    {(comment.comment_by || "?")
                      .split(" ")
                      .map((n) => n[0])
                      .slice(0, 2)
                      .join("")
                      .toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="#1A1A2E"
                      fontSize="0.82rem"
                    >
                      {comment.comment_by || "Unknown"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="#6B7280"
                      fontSize="0.7rem"
                    >
                      {comment.created_at
                        ? formatDateTime(comment.created_at)
                        : "—"}
                    </Typography>
                  </Box>
                </Box>

                {comment.commentary_type && (
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "6px",
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      color: theme.palette.primary.main,
                      fontSize: "0.68rem",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getTypeLabel(comment.commentary_type)}
                  </Box>
                )}
              </Box>

              <Typography
                variant="body2"
                color="#475467"
                fontSize="0.82rem"
                sx={{ lineHeight: 1.6 }}
              >
                {comment.comment}
              </Typography>
            </Box>
          ))}
        </Stack>
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            border: "2px dashed #E2E8F0",
            borderRadius: "10px",
            bgcolor: "#F8FAFC",
          }}
        >
          <IconButton
            size="large"
            sx={{ color: theme.palette.primary.main, mb: 0.5 }}
            disabled
          >
            <ChatBubbleOutline />
          </IconButton>
          <Typography
            variant="body2"
            color="#6B7280"
            fontSize="0.8rem"
            fontWeight={500}
          >
            No comments yet. Add the first comment above.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Commentary;